/**
 * ScopeFilterSelect — combo box for filtering findings by provenance scope.
 * A2F-473/474: Used in the filter bar of every Review page when a specific
 * submission is focused.
 *
 * A2F-761: The 'in-scope' (this sub + prior) option is no longer rendered
 * in the dropdown but the type and `applyScopeFilter` logic remain so the
 * option can be reintroduced later without a data-layer change.
 */

import type { FindingTier } from '@/lib/findingClassification';

export type ScopeFilter = 'this-sub' | 'in-scope' | 'all';

export function ScopeFilterSelect({
  value,
  onChange,
  thisSubCount,
  preExistingCount,
  inheritedCount,
}: {
  value: ScopeFilter;
  onChange: (v: ScopeFilter) => void;
  thisSubCount: number;
  preExistingCount: number;
  inheritedCount: number;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value as ScopeFilter)}
      className="px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-orange/30 focus:border-brand-orange bg-white"
    >
      <option value="this-sub">This sub only ({thisSubCount})</option>
      <option value="all">
        All scopes ({thisSubCount + preExistingCount + inheritedCount})
      </option>
    </select>
  );
}

/**
 * Apply a ScopeFilter to a list of tier-tagged items.
 *
 * - 'this-sub' → only items with tier === 'new'
 * - 'in-scope' → tier !== 'inherited' (retained for legacy state; no longer
 *   surfaced in the dropdown — A2F-761)
 * - 'all'     → all items
 */
export function applyScopeFilter<T extends { tier: FindingTier }>(
  items: T[],
  scope: ScopeFilter,
): T[] {
  if (scope === 'all') return items;
  if (scope === 'this-sub') return items.filter((i) => i.tier === 'new');
  return items.filter((i) => i.tier !== 'inherited');
}
